// Phase 4.8 (hh-9hh) — демо-галерея состояний экранов.
//
// Требование брифа (design-brief.md, «Состояния, без которых макет не
// принимаем»): у ключевых экранов должны быть визуально проверяемые состояния
// пусто / обычное / нагруженно. Открывается по ?demo=states.
//
// Здесь собрана лента ИИ — самый конкретно описанный в брифе экран: пустая,
// успешный capture, CLARIFY-переспрос, нагруженная (все типы баблов + undo),
// ошибка распознавания. Экраны, завязанные на объём данных в БД (главный,
// дерево, календарь), показывают свои состояния естественно по содержимому
// базы — у каждого есть явная обработка пусто / загрузка / загружено.

import type { JSX } from 'preact';
import { T } from '../../tokens';
import { HistoryScreen } from '../chat';
import type { Message } from '../chat/Message';

const noop = (): void => undefined;

const EMPTY_FEED: readonly Message[] = [];

const CAPTURE_FEED: readonly Message[] = [
  { id: 'c1', kind: 'user', text: 'Положил палатку в кладовку на даче.', time: '14:02' },
  {
    id: 'c2',
    kind: 'ai_summary',
    text: 'Готово — записал.',
    actions: [{ kind: 'create', name: 'палатка', to: 'Кладовка · Дача' }],
    undoable: true,
    agentActionId: 'act-1',
    time: '14:02',
  },
];

const CLARIFY_FEED: readonly Message[] = [
  { id: 'q1', kind: 'user', text: 'Положи ключи в кладовку.', time: '15:20' },
  {
    id: 'q2',
    kind: 'ai_clarify',
    text: 'Две кладовки — в какую положить ключи?',
    options: [
      { id: 'opt-dacha', label: 'Кладовка · Дача', hint: 'рыболовное, инвентарь' },
      { id: 'opt-flat', label: 'Кладовка · Квартира', hint: 'инструменты, бытовое' },
    ],
    time: '15:20',
  },
];

const ERROR_FEED: readonly Message[] = [
  { id: 'e1', kind: 'user', text: '…(неразборчиво)…', time: '16:40' },
  {
    id: 'e2',
    kind: 'ai_text',
    text: 'Не расслышал — повтори, пожалуйста. Контекст сохранил, можно просто сказать ещё раз.',
    time: '16:40',
  },
];

// «Нагруженная» лента — большой смешанный мок (user / ai_text / ai_summary с
// несколькими действиями / ai_clarify, в т.ч. закрытое окно undo).
const LOADED_FEED: readonly Message[] = [
  { id: 'l1', kind: 'user', text: 'Положил палатку и спальник в кладовку на даче.', time: '14:02' },
  {
    id: 'l2',
    kind: 'ai_summary',
    text: 'Готово — записал.',
    actions: [
      { kind: 'create', name: 'палатка', to: 'Кладовка · Дача' },
      { kind: 'create', name: 'спальник', to: 'Кладовка · Дача' },
    ],
    undoable: true,
    agentActionId: 'act-101',
    time: '14:02',
  },
  { id: 'l3', kind: 'user', text: 'А где зимняя резина?', time: '14:05' },
  { id: 'l4', kind: 'ai_text', text: 'Зимняя резина — в гараже, на стеллаже у дальней стены.', time: '14:05' },
  {
    id: 'l5',
    kind: 'user',
    text: 'Переложи удочки и фонарик в кладовку, старую палатку выкинь.',
    time: '15:12',
  },
  {
    id: 'l6',
    kind: 'ai_summary',
    text: 'Готово — обновил три записи.',
    actions: [
      { kind: 'move', name: 'удочки', from: 'Балкон', to: 'Кладовка · Дача' },
      { kind: 'move', name: 'фонарик', from: 'Гараж', to: 'Кладовка · Дача' },
      { kind: 'delete', name: 'старая палатка' },
    ],
    undoable: true,
    agentActionId: 'act-102',
    time: '15:12',
  },
  {
    id: 'l7',
    kind: 'ai_summary',
    text: 'Запись недельной давности — окно отмены закрыто.',
    actions: [{ kind: 'create', name: 'фонарик', to: 'Кладовка · Дача' }],
    undoable: false,
    time: 'вчера',
  },
];

interface StateCase {
  readonly label: string;
  readonly note: string;
  readonly feed: readonly Message[];
}

const CASES: readonly StateCase[] = [
  { label: 'Пусто', note: 'Лента ИИ — день первого запуска', feed: EMPTY_FEED },
  { label: 'Обычное', note: 'Успешный capture с резюме и undo (case-01)', feed: CAPTURE_FEED },
  { label: 'Обычное', note: 'CLARIFY-переспрос (case-28)', feed: CLARIFY_FEED },
  { label: 'Ошибка', note: 'Распознавание не удалось — «не понял, повтори»', feed: ERROR_FEED },
  { label: 'Нагруженно', note: 'Длинная лента: все типы баблов + закрытое окно undo', feed: LOADED_FEED },
];

export function StatesDemo(): JSX.Element {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bgAlt,
        padding: '24px 16px 64px',
        fontFamily: T.font,
      }}
    >
      <div style={{ maxWidth: 420, margin: '0 auto 20px' }}>
        <div style={{ fontSize: 22, fontWeight: 750, color: T.ink }}>Состояния — лента ИИ</div>
        <div style={{ fontSize: 13, color: T.ink3, marginTop: 6, lineHeight: 1.5 }}>
          Phase 4.8 — пусто / обычное / нагруженно для ленты ассистента. Экраны,
          зависящие от объёма данных (главный, дерево, календарь), показывают
          состояния по содержимому БД — у каждого есть обработка
          пусто / загрузка / загружено.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 28, alignItems: 'center' }}>
        {CASES.map((c, i) => (
          <div key={i} style={{ width: '100%', maxWidth: 420 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: '#fff',
                  background: T.ink,
                  borderRadius: 999,
                  padding: '3px 10px',
                }}
              >
                {c.label}
              </span>
              <span style={{ fontSize: 12.5, color: T.ink2, fontWeight: 500 }}>{c.note}</span>
            </div>
            <div
              style={{
                height: 560,
                background: T.bg,
                border: `1px solid ${T.borderStrong}`,
                borderRadius: 20,
                overflow: 'hidden',
                position: 'relative',
                boxShadow: T.shadow1,
              }}
            >
              <HistoryScreen
                feed={c.feed}
                onSendText={() => Promise.resolve()}
                onClarifyPick={noop}
                onOpenTree={noop}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
